import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from "reactstrap";

import ProfessionalAvatar from "../../../Components/Common/ProfessionalAvatar";
import {
  getPublicStudioProfile,
  PublicStudioProfessional,
  PublicStudioProfile,
  PublicStudioReviewSummary,
  PublicStudioService,
} from "../../../services/publicStudioService";

const formatWhatsAppUrl = (countryCode: string, phoneNumber: string): string => {
  const digits = `${countryCode}${phoneNumber}`.replace(/\D/g, "");

  return `https://wa.me/${digits}`;
};

const StudioPublicPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { slug = "" } = useParams<{ slug: string }>();

  const [profile, setProfile] = useState<PublicStudioProfile | null>(null);
  const [showPlgBadge, setShowPlgBadge] = useState(false);
  const [reviewSummary, setReviewSummary] = useState<PublicStudioReviewSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadProfile = useCallback(async (): Promise<void> => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    setNotFound(false);

    try {
      const response = await getPublicStudioProfile(slug);
      setProfile(response.data);
      setShowPlgBadge(response._metadata?.is_powered_by_adatis === true);
      setReviewSummary(response._metadata?.review_summary ?? null);
    } catch (error) {
      const apiError = error as { code?: string };

      if (apiError.code === "NOT_FOUND") {
        setNotFound(true);
        return;
      }

      setLoadError(t("public.studio.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [slug, t]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (profile?.name) {
      document.title = t("public.studio.pageTitle", { name: profile.name });
      return;
    }

    document.title = t("public.studio.pageTitleFallback");
  }, [profile?.name, t]);

  const locationLabel = useMemo(() => {
    if (!profile?.primary_unit) {
      return null;
    }

    return profile.primary_unit.name?.trim() || null;
  }, [profile?.primary_unit]);

  const whatsAppUrl = useMemo(() => {
    const unit = profile?.primary_unit;

    if (!unit?.phone_number) {
      return null;
    }

    return formatWhatsAppUrl(unit.country_code || "55", unit.phone_number);
  }, [profile?.primary_unit]);

  const bookUrl = profile?.booking_token ? `/book/${profile.booking_token}` : null;

  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center py-5">
        <Container style={{ maxWidth: 560 }}>
          <div className="text-center">
            <div className="avatar-md mx-auto mb-3">
              <span className="avatar-title rounded-circle bg-light text-danger fs-24">
                <i className="ri-link-unlink" />
              </span>
            </div>
            <h4 className="mb-2">{t("public.studio.errors.notFoundTitle")}</h4>
            <p className="text-muted mb-0">{t("public.studio.errors.notFoundDescription")}</p>
          </div>
        </Container>
      </div>
    );
  }

  if (loadError || !profile) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center py-5">
        <Container style={{ maxWidth: 560 }}>
          <Alert color="danger" className="mb-0">
            {loadError ?? t("public.studio.errors.loadFailed")}
          </Alert>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light py-4 pb-5" style={{ paddingBottom: "5.5rem" }}>
      <Container style={{ maxWidth: 960 }}>
        <section className="text-center mb-4 mb-md-5">
          <div className="mb-3">
            {profile.logo_url ? (
              <img
                src={profile.logo_url}
                alt=""
                className="rounded-circle object-fit-cover border bg-white shadow-sm"
                style={{ width: 96, height: 96 }}
              />
            ) : (
              <div
                className="mx-auto rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center shadow-sm"
                style={{ width: 96, height: 96 }}
              >
                <i className="ri-store-2-line fs-1" aria-hidden="true" />
              </div>
            )}
          </div>
          <h1 className="h3 mb-2">{profile.name}</h1>
          {reviewSummary ? (
            <p className="text-warning mb-2">
              {"★".repeat(Math.round(reviewSummary.average))}
              <span className="text-muted">
                {"★".repeat(Math.max(0, 5 - Math.round(reviewSummary.average)))}
              </span>
              <span className="text-muted ms-2 small">
                {t("public.studio.reviewSummary.label", {
                  average: reviewSummary.average.toFixed(1),
                  count: reviewSummary.count,
                })}
              </span>
            </p>
          ) : null}
          {locationLabel ? (
            <p className="text-muted mb-0">
              <i className="ri-map-pin-line align-bottom me-1" aria-hidden="true" />
              {locationLabel}
            </p>
          ) : (
            <p className="text-muted mb-0">{t("public.studio.locationUnavailable")}</p>
          )}
        </section>

        {profile.about_text?.trim() ? (
          <section className="mb-4">
            <h2 className="h5 mb-3">{t("public.studio.about")}</h2>
            <Card className="border-0 shadow-sm">
              <CardBody>
                <p className="mb-0 text-muted" style={{ whiteSpace: "pre-line" }}>
                  {profile.about_text}
                </p>
              </CardBody>
            </Card>
          </section>
        ) : null}

        {profile.club_teaser?.has_plans && profile.booking_token ? (
          <section className="mb-4">
            <Card className="border-0 shadow-sm border-primary border-opacity-25">
              <CardBody>
                <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-3">
                  <div>
                    <h2 className="h5 mb-1">{t("public.studio.clubTeaser.title")}</h2>
                    <p className="text-muted mb-0 small">
                      {t("public.studio.clubTeaser.description")}
                    </p>
                  </div>
                  <Button
                    color="primary"
                    tag={Link}
                    to={`/club/plans/${encodeURIComponent(profile.booking_token)}`}
                  >
                    {t("public.studio.clubTeaser.cta")}
                  </Button>
                </div>
                {(profile.club_teaser.plans_preview?.length ?? 0) > 0 ? (
                  <ul className="list-unstyled mb-0">
                    {profile.club_teaser.plans_preview!.map((plan) => (
                      <li
                        key={plan.id}
                        className="d-flex justify-content-between gap-2 small py-1 border-top"
                      >
                        <span>{plan.name}</span>
                        <span className="text-primary fw-medium text-nowrap">
                          {new Intl.NumberFormat(i18n.language, {
                            style: "currency",
                            currency: "BRL",
                          }).format(plan.price / 100)}
                          <span className="text-muted fw-normal">
                            {" "}
                            / {t("public_club.perMonth")}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </CardBody>
            </Card>
          </section>
        ) : null}

        <section className="mb-4">
          <h2 className="h5 mb-3">{t("public.studio.services")}</h2>
          {profile.services.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardBody className="text-center py-5">
                <p className="text-muted mb-0">{t("public.studio.servicesEmpty")}</p>
              </CardBody>
            </Card>
          ) : (
            <Row className="g-3">
              {profile.services.map((service: PublicStudioService) => (
                <Col key={service.id} xs={12} md={6}>
                  <ServiceCard service={service} />
                </Col>
              ))}
            </Row>
          )}
        </section>

        <section className="mb-4">
          <h2 className="h5 mb-3">{t("public.studio.team")}</h2>
          {profile.professionals.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardBody className="text-center py-5">
                <p className="text-muted mb-0">{t("public.studio.teamEmpty")}</p>
              </CardBody>
            </Card>
          ) : (
            <Row className="g-3">
              {profile.professionals.map((professional: PublicStudioProfessional) => (
                <Col key={professional.id} xs={12} sm={6} md={4}>
                  <TeamCard professional={professional} />
                </Col>
              ))}
            </Row>
          )}
        </section>

        <footer className="text-center pt-2 pb-3">
          <div className="d-flex flex-wrap justify-content-center gap-2 mb-3">
            {profile.instagram_url ? (
              <Button
                tag="a"
                href={profile.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                color="soft-danger"
                size="sm"
              >
                <i className="ri-instagram-line align-bottom me-1" aria-hidden="true" />
                {t("public.studio.social.instagram")}
              </Button>
            ) : null}
            {profile.primary_unit?.google_review_url ? (
              <Button
                tag="a"
                href={profile.primary_unit.google_review_url}
                target="_blank"
                rel="noopener noreferrer"
                color="soft-primary"
                size="sm"
              >
                <i className="ri-map-pin-line align-bottom me-1" aria-hidden="true" />
                {t("public.studio.social.maps")}
              </Button>
            ) : null}
            {whatsAppUrl ? (
              <Button
                tag="a"
                href={whatsAppUrl}
                target="_blank"
                rel="noopener noreferrer"
                color="soft-success"
                size="sm"
              >
                <i className="ri-whatsapp-line align-bottom me-1" aria-hidden="true" />
                {t("public.studio.social.whatsapp")}
              </Button>
            ) : null}
          </div>

          {showPlgBadge ? (
            <div className="mt-2">
              <span className="badge bg-light text-muted border px-3 py-2 fw-normal">
                {t("public.studio.powered_by")}
              </span>
            </div>
          ) : null}
        </footer>
      </Container>

      {bookUrl ? (
        <div
          className="position-fixed bottom-0 start-0 end-0 p-3 bg-white border-top shadow-sm"
          style={{ zIndex: 1030 }}
        >
          <Container style={{ maxWidth: 960 }}>
            <Button tag={Link} to={bookUrl} color="primary" size="lg" className="w-100">
              <i className="ri-calendar-check-line align-bottom me-2" aria-hidden="true" />
              {t("public.studio.book_now")}
            </Button>
          </Container>
        </div>
      ) : null}
    </div>
  );
};

interface ServiceCardProps {
  service: PublicStudioService;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ service }) => {
  const { t } = useTranslation();

  return (
    <Card className="border-0 shadow-sm h-100 mb-0">
      <CardBody className="d-flex gap-3">
        <div
          className="rounded flex-shrink-0 bg-light d-flex align-items-center justify-content-center"
          style={{ width: 72, height: 72 }}
        >
          {service.cover_url ? (
            <img
              src={service.cover_url}
              alt=""
              className="rounded object-fit-cover w-100 h-100"
            />
          ) : (
            <i className="ri-scissors-cut-line fs-24 text-muted" aria-hidden="true" />
          )}
        </div>
        <div className="flex-grow-1">
          <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-1">
            <h3 className="h6 mb-0">{service.name}</h3>
            <div className="text-end">
              <span className="fw-semibold text-primary">{service.price_display}</span>
              {service.promo_active && service.list_price_display ? (
                <small className="text-muted text-decoration-line-through d-block">
                  {service.list_price_display}
                </small>
              ) : null}
              {service.price_is_from ? (
                <small className="text-muted d-block">
                  {t("public.studio.priceFromHint")}
                </small>
              ) : null}
            </div>
          </div>
          {service.description ? (
            <p className="text-muted small mb-2">{service.description}</p>
          ) : null}
          <p className="text-muted small mb-2">
            {t("public.studio.durationMinutes", { minutes: service.duration_minutes })}
          </p>
          {service.category?.name ? (
            <Badge color="light" className="text-muted border">
              {service.category.name}
            </Badge>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
};

interface TeamCardProps {
  professional: PublicStudioProfessional;
}

const TeamCard: React.FC<TeamCardProps> = ({ professional }) => (
  <Card className="border-0 shadow-sm h-100 mb-0">
    <CardBody className="text-center">
      <div className="d-flex justify-content-center mb-3">
        <ProfessionalAvatar
          name={professional.name}
          colorHex={professional.color_hex}
          photoUrl={professional.photo_url}
          size={72}
        />
      </div>
      <h3 className="h6 mb-1">{professional.name}</h3>
      {professional.public_bio ? (
        <p className="text-muted small mb-0">{professional.public_bio}</p>
      ) : null}
    </CardBody>
  </Card>
);

export default StudioPublicPage;
